import { useState } from "react";
import { Link } from "react-router-dom";
import { getCookieConsent, resetCookieConsent } from "../components/CookieConsent";

/** Política de cookies (RGPD / LSSI). Página pública, honesta con la realidad
 *  técnica: HelixScan no usa cookies de terceros ni de publicidad; el
 *  almacenamiento es esencial (localStorage) salvo la analítica opt-in futura. */

const TEAL = "#0891b2";
const INK = "#0c2733";
const MUTED = "rgba(12,39,51,0.68)";

const STORAGE_ITEMS = [
  ["helixscan-auth", "Esencial", "Mantiene tu sesión iniciada (token de acceso). Se borra al cerrar sesión."],
  ["theme", "Esencial", "Recuerda si prefieres el modo claro u oscuro."],
  ["hx_tour_done:*", "Esencial", "Recuerda que ya viste el tutorial de inicio para no repetirlo."],
  ["hx_onb_skip:*", "Esencial", "Recuerda que pospusiste el asistente de configuración."],
  ["itf_last_seen_version", "Esencial", "Recuerda la última versión vista para mostrarte las novedades una sola vez."],
  ["hx_cookie_consent", "Esencial", "Guarda tu elección sobre este propio aviso de cookies."],
];

export default function CookiePolicyPage() {
  const [consent, setConsent] = useState(getCookieConsent());

  const h2: React.CSSProperties = { fontSize: 20, fontWeight: 700, margin: "34px 0 10px", color: INK };
  const p: React.CSSProperties = { fontSize: 15, lineHeight: 1.7, color: MUTED, margin: "0 0 12px" };

  return (
    <div style={{ background: "#f6fafc", minHeight: "100vh", fontFamily: "'Inter', system-ui, sans-serif", color: INK }}>
      <header style={{ borderBottom: "1px solid rgba(12,39,51,0.1)", background: "#fff" }}>
        <div style={{ maxWidth: 860, margin: "0 auto", padding: "0 24px", height: 64, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Link to="/"><img src="/helixscan-logo.png" alt="HelixScan" style={{ height: 32, display: "block" }} /></Link>
          <Link to="/" style={{ color: TEAL, textDecoration: "none", fontSize: 14, fontWeight: 600 }}>← Volver</Link>
        </div>
      </header>

      <main style={{ maxWidth: 860, margin: "0 auto", padding: "48px 24px 80px" }}>
        <h1 style={{ fontSize: 32, fontWeight: 700, letterSpacing: "-0.02em", margin: "0 0 8px" }}>Política de cookies</h1>
        <p style={{ ...p, fontSize: 13.5 }}>Última actualización: junio de 2026 · Responsable: Haltrix Partners (HelixScan) · Contacto: haltrixpartners@gmail.com</p>

        <h2 style={h2}>Lo esencial, en una frase</h2>
        <p style={p}>
          HelixScan <strong>no utiliza cookies de publicidad ni de seguimiento</strong>, ni comparte
          datos de navegación con terceros. Usamos únicamente almacenamiento técnico en tu navegador
          (localStorage) imprescindible para que la plataforma funcione: mantener tu sesión y recordar
          tus preferencias.
        </p>

        <h2 style={h2}>¿Qué almacenamos exactamente?</h2>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, background: "#fff", borderRadius: 12, overflow: "hidden" }}>
            <thead>
              <tr style={{ background: TEAL, color: "#fff", textAlign: "left" }}>
                <th style={{ padding: "10px 14px" }}>Elemento</th>
                <th style={{ padding: "10px 14px" }}>Tipo</th>
                <th style={{ padding: "10px 14px" }}>Finalidad</th>
              </tr>
            </thead>
            <tbody>
              {STORAGE_ITEMS.map(([k, t, d]) => (
                <tr key={k} style={{ borderBottom: "1px solid rgba(12,39,51,0.08)" }}>
                  <td style={{ padding: "10px 14px", fontFamily: "monospace", fontSize: 12.5, whiteSpace: "nowrap" }}>{k}</td>
                  <td style={{ padding: "10px 14px" }}>{t}</td>
                  <td style={{ padding: "10px 14px", color: MUTED }}>{d}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p style={{ ...p, marginTop: 12 }}>
          El almacenamiento esencial no requiere consentimiento según la normativa (art. 22.2 LSSI),
          pero te informamos de él por transparencia. No instalamos ninguna cookie de analítica ni de
          publicidad; si en el futuro añadimos medición de uso, <strong>solo se activará si la aceptaste
          expresamente</strong> en el aviso de cookies.
        </p>

        <h2 style={h2}>Servicios de terceros</h2>
        <p style={p}>
          Las tipografías de la web se cargan desde Google Fonts, lo que implica que tu navegador
          solicita esos archivos a servidores de Google (que recibe tu dirección IP como en cualquier
          descarga web). Google Fonts no instala cookies.
        </p>

        <h2 style={h2}>Tu elección actual</h2>
        <div style={{ background: "#fff", border: "1px solid rgba(12,39,51,0.1)", borderRadius: 14, padding: "18px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <span style={{ fontSize: 14.5 }}>
            {consent
              ? <>Analítica: <strong>{consent.analytics ? "aceptada" : "rechazada"}</strong> <span style={{ color: MUTED, fontSize: 13 }}>({new Date(consent.ts).toLocaleDateString("es-ES")})</span></>
              : "Aún no has guardado una preferencia."}
          </span>
          <button onClick={() => { resetCookieConsent(); setConsent(null); }} style={{
            background: "#fff", color: TEAL, border: `1px solid ${TEAL}`, borderRadius: 9,
            padding: "9px 16px", fontSize: 13.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
          }}>
            Cambiar preferencias
          </button>
        </div>

        <h2 style={h2}>Cómo borrar el almacenamiento</h2>
        <p style={p}>
          Puedes eliminar en cualquier momento estos datos desde la configuración de tu navegador
          (borrar datos de sitios web para helixscan.haltrixpartners.com). Ten en cuenta que al hacerlo
          se cerrará tu sesión.
        </p>
      </main>
    </div>
  );
}
